import React from 'react';
import { useBffClient } from '../api/client';
import type { UpsellSurface } from '../plan/free-tier';
import { usePlan } from './use-plan';

/**
 * Reports that an upsell surface was seen or acted on (story #137).
 *
 * Instrumented as the surfaces are written rather than retrofitted, because "which surface precedes an
 * upgrade" cannot be answered later from data nobody collected.
 *
 * NOTHING HERE CAN FAIL THE PAGE. Every call swallows its error: a banner that renders a console error
 * because its telemetry call failed is worse than the missing data point, and the BFF returns 204
 * regardless for the same reason.
 */
export function useUpsellReporter() {
  const client = useBffClient();
  const { isPersonal } = usePlan();

  return React.useCallback(
    (surface: UpsellSurface, clicked = false) => {
      // Only the free tier has upsells. Reporting for a team would be noise in the funnel, and the
      // server's cohort is built from personal organizations anyway.
      if (!isPersonal) return;

      void client
        .post('/bff/organizations/current/funnel/upsell', { surface, clicked })
        .catch(() => {});
    },
    [client, isPersonal],
  );
}

/**
 * Reports an upsell surface as seen, once, when it mounts.
 *
 * Once per mount rather than once per render: the server counts occurrences, and a component that
 * re-rendered on every keystroke would report a number about React rather than about the person.
 */
export function useReportUpsellShown(surface: UpsellSurface, active = true) {
  const report = useUpsellReporter();
  const reported = React.useRef(false);

  React.useEffect(() => {
    if (!active || reported.current) return;
    reported.current = true;
    report(surface);
  }, [active, report, surface]);
}
